import type { Code, Root } from 'mdast';
import { toString } from 'mdast-util-to-string';

import { getAddedDiffLines, getChangedCodeLines } from './changed-code-lines';

export type CodeHighlight = {
  section: string;
  fixSection?: string;
  filePath: string;
  code: string | null | undefined;
};

function getFilename(node: Code): string | undefined {
  const meta = node.meta?.trim() ?? '';
  const namedFile = /\b(?:filename|file|path|title)=['"]([^'"]+)['"]/i.exec(meta)?.[1];
  if (namedFile) {
    return namedFile;
  }
  if (meta.includes('.') && !/\s/.test(meta)) {
    return meta;
  }
  const separator = node.lang?.indexOf(':') ?? -1;
  return separator >= 0 ? node.lang?.slice(separator + 1) : undefined;
}

function getSectionCodeBlocks(tree: Root, highlight: CodeHighlight): Code[] {
  let sectionDepth: number | undefined;
  const blocks: Code[] = [];
  for (const node of tree.children) {
    if (node.type === 'heading') {
      if (toString(node).trim().toLowerCase() === highlight.section.toLowerCase()) {
        sectionDepth = node.depth;
      } else if (sectionDepth && node.depth <= sectionDepth) {
        sectionDepth = undefined;
      }
    }
    if (!sectionDepth || node.type !== 'code') {
      continue;
    }
    const filename = getFilename(node);
    if (!filename || filename === highlight.filePath) {
      blocks.push(node);
    }
  }
  return blocks;
}

function highlightFixes(tree: Root, highlight: CodeHighlight, originalBlocks: Code[]) {
  if (!highlight.fixSection) {
    return;
  }
  const fixes = getSectionCodeBlocks(tree, { ...highlight, section: highlight.fixSection });
  const isDiffBlock = (node: Code) => {
    const language = node.lang?.split(':')[0];
    return language === 'diff' || language === 'patch';
  };
  const nonDiffFixCount = fixes.filter((node) => !isDiffBlock(node)).length;
  const originalBlock = originalBlocks.length === 1 ? originalBlocks[0] : undefined;
  for (const node of fixes) {
    let highlightedLines: number[];
    if (isDiffBlock(node)) {
      highlightedLines = getAddedDiffLines(node.value);
    } else {
      if (!originalBlock || nonDiffFixCount !== 1) {
        continue;
      }
      highlightedLines = getChangedCodeLines(originalBlock.value, node.value);
    }
    if (highlightedLines.length === 0) {
      continue;
    }
    node.data = {
      ...node.data,
      hProperties: {
        ...node.data?.hProperties,
        'data-highlighted-lines': highlightedLines,
        'data-highlight-tone': 'success',
      },
    };
  }
}

export function remarkCodeHighlight(highlight?: CodeHighlight) {
  return (tree: Root) => {
    if (!highlight) {
      return;
    }
    const originalBlocks = getSectionCodeBlocks(tree, highlight);
    highlightFixes(tree, highlight, originalBlocks);
    if (!highlight?.code?.trim()) {
      return;
    }
    const target = highlight.code
      .trim()
      .split('\n')
      .map((line) => line.trim());
    const matches: Array<{ node: Code; start: number }> = [];
    for (const node of originalBlocks) {
      const lines = node.value.split('\n').map((line) => line.trim());
      lines.forEach((_, index) => {
        if (target.every((line, offset) => line === lines[index + offset])) {
          matches.push({ node, start: index + 1 });
        }
      });
    }
    const match = matches[0];
    if (matches.length !== 1 || !match) {
      return;
    }
    const { node, start } = match;
    node.data = {
      ...node.data,
      hProperties: {
        ...node.data?.hProperties,
        'data-highlighted-lines': target.map((_, index) => start + index),
      },
    };
  };
}
